import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useAuth } from './AuthContext';
import '../stili/StaffForm.css';
import TimedPopup from './TimedPopup';

const API_URL = process.env.REACT_APP_API_URL;

const initialFormData = {
  nome: '',
  cognome: '',
  telefono: '',
  email: '',
  numeroTesserino: '',
  categorieSelezionate: [],
  categoriaSelezionata: '',
  creaAccount: true,
  password: ''
};

export default function StaffForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [categorie, setCategorie] = useState([]);
  const [formData, setFormData] = useState(initialFormData);

  const [message, setMessage] = useState('');
  const [passwordGenerata, setPasswordGenerata] = useState(null);
  const [copied, setCopied] = useState(false);

  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  const categorieById = useMemo(() => {
    const m = new Map();
    (categorie || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [categorie]);

  useEffect(() => {
    async function fetchCategorie() {
      setMessage('');
      setPasswordGenerata(null);

      if (user?.accessToken) {
        setHasAccess(true);
        try {
          const res = await authFetch(`${API_URL}/api/admin/lookup/categorie`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          const data = await res.json();
          setCategorie(Array.isArray(data) ? data : []);
        } catch (err) {
          setCategorie([]);
          setMessage('❌ Impossibile caricare le categorie');
        }
      } else {
        setHasAccess(false);
      }

      setAccessChecked(true);
    }

    fetchCategorie();
  }, [user, authFetch]);

  if (!accessChecked) {
    return (
      <div className="staff-form-container" translate="no">
        <div className="staff-message">Caricamento...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <TimedPopup
        message="Solo utenti loggati possono accedere a questo modulo."
        duration={5000}
      />
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'creaAccount' && !checked) {
        next.password = '';
      }

      return next;
    });
  };

  const aggiungiCategoria = () => {
    const sel = formData.categoriaSelezionata;

    if (!sel) return;

    if (formData.categorieSelezionate.includes(sel)) {
      setMessage('❌ Categoria già selezionata');
      return;
    }

    setMessage('');
    setFormData((prev) => ({
      ...prev,
      categorieSelezionate: [...prev.categorieSelezionate, sel],
      categoriaSelezionata: ''
    }));
  };

  const rimuoviCategoria = (index) => {
    setFormData((prev) => ({
      ...prev,
      categorieSelezionate: prev.categorieSelezionate.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (!passwordGenerata) return;

    try {
      await navigator.clipboard.writeText(passwordGenerata);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setMessage('❌ Impossibile copiare la password');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setPasswordGenerata(null);
    setCopied(false);

    if (!user?.accessToken) {
      setMessage('❌ Token mancante, effettua login');
      return;
    }

    if (formData.categorieSelezionate.length === 0) {
      setMessage('❌ Selezionare almeno una categoria');
      return;
    }

    if (
      formData.creaAccount &&
      formData.password &&
      formData.password.trim().length > 0 &&
      formData.password.trim().length < 8
    ) {
      setMessage('❌ La password deve avere almeno 8 caratteri');
      return;
    }

    const payload = {
      nome: formData.nome.trim(),
      cognome: formData.cognome.trim(),
      telefono: formData.telefono?.trim() ? formData.telefono.trim() : null,
      email: formData.email.trim(),
      numeroTesserino: formData.numeroTesserino?.trim()
        ? formData.numeroTesserino.trim()
        : null,
      idCategorie: formData.categorieSelezionate.map((id) => Number(id)),
      creaAccount: !!formData.creaAccount,
      password:
        formData.creaAccount && formData.password?.trim()
          ? formData.password.trim()
          : null
    };

    try {
      const res = await authFetch(`${API_URL}/api/admin/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const rawText = await res.text();
      let data = {};

      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = { message: rawText };
      }

      if (!res.ok) {
        if (res.status === 409) {
          const backendMsg = data?.message || data?.error || rawText || '';
          const msgPulito =
            backendMsg && backendMsg.trim().length > 0
              ? backendMsg
              : 'Conflitto: email o numero tesserino già presenti.';
          throw new Error(msgPulito);
        }

        throw new Error(data?.message || data?.error || `Errore HTTP ${res.status}`);
      }

      if (data?.passwordGenerata) {
        setPasswordGenerata(data.passwordGenerata);
        setMessage('✅ Staff creato. Annotare la password prima di uscire.');
        resetForm();
        return;
      }

      setMessage('✅ Staff inserito con successo!');
      resetForm();
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    }
  };

  return (
    <div className="staff-form-container" translate="no">
      <h2 className="staff-form-title">Inserisci Nuovo Staff</h2>

      <form className="staff-form" onSubmit={handleSubmit}>
        <div className="staff-sezione">
          <h3 className="staff-sezione-titolo">Dati anagrafici</h3>

          <div className="staff-form-grid">
            <div className="staff-form-group">
              <label className="staff-label">Nome</label>
              <input
                className="staff-input"
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                autoComplete="given-name"
                required
              />
            </div>

            <div className="staff-form-group">
              <label className="staff-label">Cognome</label>
              <input
                className="staff-input"
                type="text"
                name="cognome"
                value={formData.cognome}
                onChange={handleChange}
                autoComplete="family-name"
                required
              />
            </div>

            <div className="staff-form-group">
              <label className="staff-label">Telefono</label>
              <input
                className="staff-input"
                type="text"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                autoComplete="tel"
                placeholder="Facoltativo"
              />
            </div>

            <div className="staff-form-group">
              <label className="staff-label">Email</label>
              <input
                className="staff-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>

            <div className="staff-form-group">
              <label className="staff-label">Numero Tesserino</label>
              <input
                className="staff-input"
                type="text"
                name="numeroTesserino"
                value={formData.numeroTesserino}
                onChange={handleChange}
                placeholder="Facoltativo"
              />
            </div>
          </div>
        </div>

        <div className="staff-sezione">
          <h3 className="staff-sezione-titolo">Categorie di competenza</h3>

          <div className="staff-form-grid">
            <div className="staff-form-group staff-full-width">
              <label className="staff-label">Categorie di competenza</label>

              <div className="staff-categoria-add-row">
                <select
                  className="staff-select"
                  name="categoriaSelezionata"
                  value={formData.categoriaSelezionata}
                  onChange={handleChange}
                >
                  <option value="">-- Seleziona categoria --</option>
                  {categorie.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="staff-mini-button"
                  onClick={aggiungiCategoria}
                  title="Aggiungi categoria"
                >
                  +
                </button>
              </div>

              <div className="staff-categorie-list">
                {formData.categorieSelezionate.length === 0 ? (
                  <div className="staff-empty-state">
                    Nessuna categoria selezionata
                  </div>
                ) : (
                  formData.categorieSelezionate.map((catId, i) => {
                    const cat = categorieById.get(String(catId));

                    return (
                      <div key={`${catId}-${i}`} className="staff-categoria-item">
                        <span>{cat ? cat.nome : catId}</span>
                        <button
                          type="button"
                          className="staff-mini-button"
                          onClick={() => rimuoviCategoria(i)}
                          title="Rimuovi categoria"
                        >
                          -
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="staff-sezione">
          <h3 className="staff-sezione-titolo">Account utente</h3>

          <div className="staff-form-grid">
            <div className="staff-form-group staff-full-width">
              <label className="staff-checkbox-label">
                <input
                  type="checkbox"
                  name="creaAccount"
                  checked={formData.creaAccount}
                  onChange={handleChange}
                />
                <span>Crea account utente</span>
              </label>
            </div>

            {formData.creaAccount && (
              <div className="staff-form-group staff-full-width">
                <label className="staff-label">Password (opzionale)</label>
                <input
                  className="staff-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Lascia vuoto per farla generare"
                  minLength={8}
                  maxLength={100}
                />
              </div>
            )}
          </div>
        </div>

        {passwordGenerata && (
          <div className="staff-message staff-success">
            <div className="staff-password-box">
              <span>
                🔐 Password generata: <b>{passwordGenerata}</b>
              </span>

              <button
                type="button"
                className="staff-mini-button"
                onClick={handleCopyPassword}
              >
                {copied ? 'Copiata' : 'Copia'}
              </button>
            </div>
          </div>
        )}

        {message && (
          <div
            className={`staff-message ${
              message.startsWith('✅') ? 'staff-success' : 'staff-error'
            }`}
          >
            {message}
          </div>
        )}

        <div className="staff-button-row">
          <button className="staff-custom-button" type="submit">
            Crea Staff
          </button>

          <button
            className="staff-custom-button"
            type="button"
            onClick={() => navigate('/gestionale')}
          >
            Torna al Gestionale
          </button>
        </div>
      </form>
    </div>
  );
}